import KYC from 'kyc-core'
import { IDocumentQueryParams } from 'kyc-core/dist/Interfaces/Documents'
import { IQueryParams } from 'kyc-core/dist/Interfaces/Request'
import { IRequest } from 'kyc-core/dist/Interfaces'
import {
  AUTH_TOKEN,
  BASE_URL,
  DOCUMENT,
  EXTENDED_AUTH_TOKEN,
  LOCALSTORAGE_ITEM,
} from '../enums/common'
import {
  generateUploadDocumentInput,
  generateUploadDocumentsInput,
  handleError,
} from './utils'
import {
  IDeleteDocumentPayload,
  IDocInfo,
  ISoftDeletedDocumentsPayload,
} from '../interfaces/Upload'
import { IFormData } from '../interfaces/Folder'

const kycCore = new KYC({
  baseUrl: BASE_URL || '',
  auth: {
    type: 'localStorage',
    localStorageItem: LOCALSTORAGE_ITEM || '',
    authTokenHeader: AUTH_TOKEN || '',
    extendAuthTokenHeader: EXTENDED_AUTH_TOKEN || '',
  },
})

export const getInitialData = ({
  history,
}: {
  history: any
}): Promise<[Record<string, string>, Record<string, string>]> => {
  const { folder, breadcrumb } = kycCore

  return Promise.all([
    folder.getFolder(),
    breadcrumb.getBreadCrumb(),
  ]).catch((error) => handleError(error, history, true)) as Promise<
    [Record<string, string>, Record<string, string>]
  >
}

export default kycCore

export const getDocuments = (
  docInfoParams: IDocInfo[],
  rawParams?: boolean
): Promise<Record<string, string>[]> => {
  const { documents } = kycCore
  const params = rawParams
    ? docInfoParams.map(({ typeId, personTypeId }, index) => ({
        typeId: String(typeId),
        personTypeId: String(personTypeId),
        multiFileIndex: String(index),
        limit: 5,
      }))
    : generateGetDocumentsParams(docInfoParams)
  const promise: Promise<Response | Record<string, any>>[] = []

  docInfoParams.forEach((param, index) => {
    const doc = documents.getDocuments(params[index])
    promise.push(doc)
  })

  return Promise.all(promise) as Promise<Record<string, string>[]>
}

export const getDocumentsDetails = async (
  docInfoParams: IDocInfo[]
): Promise<Record<string, string>[]> => {
  const { request, handleResponse } = kycCore
  const params = generateGetDocumentsParams(docInfoParams)
  const promise = []

  const queryParams: IQueryParams = { ...params[0] } as IQueryParams
  const response = await request.request({
    endpoint: 'api/documents/details',
    queryParams: { ...queryParams },
  })
  const rectoFile = handleResponse.handleJSONResponse(response)

  promise.push(rectoFile)
  if (docInfoParams.length > 1) {
    const queryParamsVerso: IQueryParams = { ...params[1] } as IQueryParams
    const responseVerso = await request.request({
      endpoint: 'api/documents/details',
      queryParams: { ...queryParamsVerso },
    })
    const versoFile = handleResponse.handleJSONResponse(responseVerso)
    promise.push(versoFile)
  }

  return Promise.all(promise) as Promise<Record<string, string>[]>
}
export const getDocumentsByTypeIds = async (types: number[]) => {
  const { request, handleResponse } = kycCore

  const res = await request.request({
    endpoint: `api/documents/get-by-types?${generateGetDocumentsByTypeIdsParams(
      types
    )}&limit=50`,
    method: 'GET',
  })

  return handleResponse.handleJSONResponse(res)
}
export const uploadDocuments = async (
  step: number,
  files: File[],
  docInfoParams: IDocInfo[],
  identityFiles: boolean = false
): Promise<Record<string, string> | any> => {
  const { documents } = kycCore

  const identityDocumentWithVerso =
    identityFiles && files.length > 1 && files[1] && files[1].type

  if (identityDocumentWithVerso) {
    const params = generateUploadDocumentsInput({
      ...docInfoParams[0],
      currentStep: step,
      files,
    })
    // @ts-ignore
    return documents.uploadDocuments(params)
  }

  return Promise.all(
    files
      .filter((file) => !!file)
      .map((file, index) => {
        const paramsOption = {
          ...docInfoParams[index],
          currentStep: step,
          files: files[index],
        }

        const params = generateUploadDocumentInput(paramsOption)
        // @ts-ignore
        return documents.uploadDocument(params)
      })
  )
}

const generateGetDocumentsParams = (
  data: IDocInfo[]
): IDocumentQueryParams[] => {
  const [firstDocument, secondDocument, thirdDoc] = data
  let params: IDocumentQueryParams[] = [
    {
      typeId: firstDocument.typeId.toString(),
      personTypeId: firstDocument.personTypeId.toString(),
      multiFileIndex: DOCUMENT.FRONT,
    },
  ]
  if (data.length > 1) {
    params = [
      ...params,
      {
        typeId: secondDocument.typeId.toString(),
        personTypeId: secondDocument.personTypeId.toString(),
        multiFileIndex:
          secondDocument.typeId.toString() === firstDocument.typeId.toString()
            ? DOCUMENT.BACK
            : DOCUMENT.FRONT,
      },
      ...(thirdDoc
        ? [
            {
              typeId: thirdDoc.typeId.toString(),
              personTypeId: thirdDoc.personTypeId.toString(),
              multiFileIndex: DOCUMENT.FRONT,
            },
          ]
        : []),
    ]
  }
  return params
}

export const generateGetDocumentsByTypeIdsParams = (types: number[]) => {
  return types.map((type) => `typeIds[]=${type}`).join('&')
}

export const getUserAddress = async () => {
  const { request, handleResponse } = kycCore

  const res = await request.request({
    endpoint: 'api/lbp/jwt/get-person-address',
  })

  return handleResponse.handleJSONResponse(res)
}

export const getJddAddress = async () => {
  const { request, handleResponse } = kycCore

  const res = await request.request({
    endpoint: 'api/lbp/jwt/jdd-address',
  })

  return handleResponse.handleJSONResponse(res)
}

export const saveJddAddress = async (data: Record<string, any>) => {
  const { request, handleResponse } = kycCore
  const params: IRequest = {
    endpoint: 'api/lbp/jwt/save-address',
    method: 'POST',
    data,
  }
  const response = await request.request(params)

  return handleResponse.handleNoContentResponse(response)
}

export const saveFolder = async (formData: IFormData) => {
  const { folder, handleResponse } = kycCore
  const response = (await folder.saveFolder(formData, true)) as Response

  if (response.status && response.status === 400) {
    // for 400 http code, the error is in the response so we need to show it
    const jsonResponse = await handleResponse.handleJSONResponse(
      response,
      false
    )
    throw new Error(jsonResponse.error)
  }

  // handle the response as usual
  await handleResponse.handleNoContentResponse(response)
}

export const getCitiesList = async (city?: string, postalCode?: string) => {
  const { request, handleResponse } = kycCore
  const cityQueryParameter = city ? { ladac6: city } : undefined
  const postalCodeQueryParameters = postalCode
    ? { cadpo: postalCode }
    : undefined

  const res = await request.request({
    endpoint: 'api/lbp/communes-v2',
    queryParams: { ...cityQueryParameter, ...postalCodeQueryParameters },
  })

  return handleResponse.handleJSONResponse(res)
}

export const getCountriesList = async () => {
  const { request, handleResponse } = kycCore

  const res = await request.request({
    endpoint: 'api/lbp/fiscal-countries',
  })

  return handleResponse.handleJSONResponse(res)
}

export const softDeleteDocument = async (
  data: ISoftDeletedDocumentsPayload[]
) => {
  const { request, handleResponse } = kycCore
  const params: IRequest = {
    endpoint: 'api/documents',
    method: 'DELETE',
    data,
  }
  const response = await request.request(params)

  return handleResponse.handleNoContentResponse(response)
}

export const deleteDocument = async (data: IDeleteDocumentPayload) => {
  const { request, handleResponse } = kycCore
  const params: IRequest = {
    endpoint: `api/documents/documentUid/${data.documentUid}`,
    method: 'DELETE',
  }
  const response = await request.request(params)

  return handleResponse.handleNoContentResponse(response)
}

export const getDocumentPreview = async (
  documentUid: string,
  isVerso: boolean = false
) => {
  const { request } = kycCore

  const hasVerso = isVerso ? '?isVerso=true' : ''

  const params: IRequest = {
    endpoint: `api/documents/content/documentUid/${documentUid}${hasVerso}`,
    method: 'GET',
  }
  return request.request(params)
}

export const getResidentialCountries = async () => {
  const { request, handleResponse } = kycCore

  const res = await request.request({
    endpoint: 'api/lbp/residential-countries',
  })

  return handleResponse.handleJSONResponse(res)
}

export const checkLbpToken = async (kyc_token: string) => {
  const { auth, handleResponse } = kycCore

  const res = await auth.request({
    endpoint: 'api/2fa/check-token',
    method: 'POST',
    data: { kyc_token },
    skipAuth: true,
  })

  return handleResponse.handleJSONResponse(res)
}

export const getAgencyParameter = async (code: string) => {
  const { request, handleResponse } = kycCore
  const res = await request.request({
    endpoint: 'api/lbp/agency-parameter',
    method: 'GET',
    queryParams: { code },
  })

  return handleResponse.handleJSONResponse(res)
}
